import { useQuery } from "@tanstack/react-query";
import { fetchQuizAnswerSheet } from "../services/quizAnswerSheetService";

function getQuestionStatus(item = {}) {
  if (Number(item?.is_question_answered || 0) !== 1) {
    return "unsolved";
  }

  return String(item?.is_answer_correct || "").toUpperCase() === "Y"
    ? "correct"
    : "incorrect";
}

function getOptionType(question = {}, option = {}) {
  const isAnswered = Number(question?.is_question_answered || 0) === 1;
  const isSelected = Number(option?.user_selected_option || 0) === 1;
  const isCorrect = String(option?.is_option_correct || "").toUpperCase() === "Y";

  if (!isAnswered) {
    return "normal";
  }
  if (isCorrect) {
    return "correct";
  }
  if (isSelected && !isCorrect) {
    return "wrong";
  }
  return "normal";
}

function normalizeQuestion(item = {}) {
  return {
    id: Number(item?.question_id || 0),
    serialNo: Number(item?.question_index || 0),
    questionIndex: Number(item?.question_index || 0),
    status: getQuestionStatus(item),
    time: item?.time_taken || "00:00:00",
    question:
      item?.question_name_en ||
      item?.question_name ||
      item?.question_name_hi ||
      "Question not available.",
    questionHtml:
      item?.question_name_en ||
      item?.question_name ||
      item?.question_name_hi ||
      "Question not available.",
    explanation:
      item?.ref_message ||
      item?.right_explanation ||
      item?.question_statement_one ||
      "",
    isGuess: Number(item?.is_guess || 0) === 1,
    isLiked: Number(item?.like_status || 0) === 1,
    isBookmarked: Number(item?.bookmark_status || 0) === 1,
    likeCount: Number(item?.total_liked || 0),
    viewCount: Number(item?.total_views || 0),
    reportCount: String(item?.total_reports || "0"),
    notesCount: Number(item?.total_notes || 0),
    bookmarkStatus: Number(item?.bookmark_status || 0) === 1,
    options: Array.isArray(item?.question_option)
      ? item.question_option.map((option, index) => ({
          id: Number(option?.option_id || index + 1),
          key: String.fromCharCode(65 + index),
          english: option?.option_name || "",
          hindi: "",
          type: getOptionType(item, option),
        }))
      : [],
  };
}

export function useQuizAnswerSheet(params = {}, options = {}) {
  return useQuery({
    queryKey: ["quizAnswerSheet", params],
    queryFn: async () => {
      const firstPageResponse = await fetchQuizAnswerSheet({
        ...params,
        page: 1,
      });
      const firstPayload = firstPageResponse.payload || {};
      const pagination = firstPayload?.pagination || {};
      const totalPages = Number(pagination?.totalPages || pagination?.total_pages || 1);
      let allQuestions = Array.isArray(firstPayload?.questions_list)
        ? [...firstPayload.questions_list]
        : [];

      for (let page = 2; page <= totalPages; page += 1) {
        const nextResponse = await fetchQuizAnswerSheet({
          ...params,
          page,
        });
        const nextPayload = nextResponse.payload || {};
        const nextQuestions = Array.isArray(nextPayload?.questions_list)
          ? nextPayload.questions_list
          : [];
        allQuestions = [...allQuestions, ...nextQuestions];
      }

      return {
        totalMarks:
          firstPayload?.total_marks ||
          `${firstPayload?.summary?.score || 0}/${firstPayload?.summary?.max_score || 0}`,
        rank: firstPayload?.rank || "-",
        timeTaken:
          firstPayload?.time_taken ||
          firstPayload?.performance_insights?.total_time_taken_formatted ||
          "00:00:00",
        totalQuestions:
          Number(firstPayload?.total_questions || firstPayload?.summary?.total_questions || 0),
        correctQuestions:
          Number(firstPayload?.correct_questions || firstPayload?.summary?.correct_answers || 0),
        incorrectQuestions:
          Number(firstPayload?.incorrect_questions || firstPayload?.summary?.wrong_answers || 0),
        notAttemptedQuestions:
          Number(
            firstPayload?.not_attempted_questions || firstPayload?.summary?.skipped_answers || 0
          ),
        quizDetail: firstPayload?.quiz_detail || {},
        questions: allQuestions.map(normalizeQuestion),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
